import { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Divider,
  Group,
  Modal,
  NumberInput,
  ScrollArea,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconCheck, IconPlus, IconSearch } from "@tabler/icons-react";
import { FormModalFooter } from "@/components/FormModalFooter";
import { ModalTitle } from "@/components/ModalTitle";

const TR = new Intl.Collator("tr");

const byRankThenName = (a, b) => a.rank - b.rank || TR.compare(a.name, b.name);

const ItemRow = memo(function ItemRow({ item, value, onChange }) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="sm" py={6}>
      <div style={{ minWidth: 0 }}>
        <Text size="sm" fw={Number(value) > 0 ? 600 : 400} truncate>
          {item.name}
        </Text>
        <Text size="xs" c="dimmed">
          {item.unit}
        </Text>
      </div>
      <NumberInput
        w={96}
        size="sm"
        min={0}
        decimalScale={2}
        hideControls
        allowNegative={false}
        placeholder="0"
        aria-label={`${item.name} miktarı`}
        value={value ?? ""}
        onChange={(next) => onChange(item.id, next)}
      />
    </Group>
  );
});

export function RequestComposer({
  opened,
  onClose,
  branches,
  items,
  categories,
  sending,
  onSubmit,
}) {
  const mobile = useMediaQuery("(max-width: 48em)");
  const [branchId, setBranchId] = useState("");
  const [qty, setQty] = useState({});
  const [note, setNote] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (opened) {
      setQty({});
      setNote("");
      setSearch("");
    }
  }, [opened]);

  useEffect(() => {
    if (opened && !branchId && branches.length) {
      setBranchId(branches[0].value);
    }
  }, [opened, branchId, branches]);

  const groups = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("tr");
    const visible = items.filter(
      (item) =>
        item.isActive &&
        (!term || item.name.toLocaleLowerCase("tr").includes(term)),
    );
    const known = new Set(categories.map((category) => category.id));
    const listed = categories
      .map((category) => ({
        key: category.id,
        name: category.name,
        items: visible
          .filter((item) => item.categoryId === category.id)
          .sort(byRankThenName),
      }))
      .filter((group) => group.items.length);
    const rest = visible
      .filter((item) => !known.has(item.categoryId))
      .sort(byRankThenName);

    return rest.length
      ? [
          ...listed,
          {
            key: "other",
            name: listed.length ? "Diğer" : "Kalemler",
            items: rest,
          },
        ]
      : listed;
  }, [items, categories, search]);

  const lines = useMemo(
    () =>
      Object.entries(qty)
        .filter(([, value]) => Number(value) > 0)
        .map(([itemId, value]) => ({
          itemId: Number(itemId),
          requestedQty: Number(value),
        })),
    [qty],
  );

  const filledIn = (group) =>
    group.items.filter((item) => Number(qty[item.id]) > 0).length;

  const change = useCallback(
    (itemId, value) => setQty((prev) => ({ ...prev, [itemId]: value })),
    [],
  );

  const submit = (event) => {
    event.preventDefault();

    if (branchId && lines.length) {
      onSubmit({
        branchId: Number(branchId),
        note: note.trim() || null,
        lines,
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<ModalTitle icon={IconPlus} title="İkmal talebi" />}
      size="xl"
      fullScreen={mobile}
    >
      <form onSubmit={submit}>
        <Stack gap="sm">
          <Group grow align="flex-end">
            <Select
              label="Şube"
              placeholder="Talebi açan şube"
              allowDeselect={false}
              data={branches}
              value={branchId}
              onChange={(value) => setBranchId(value ?? "")}
            />
            <TextInput
              label="Kalem ara"
              placeholder="Ürün adı"
              leftSection={<IconSearch size={15} />}
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
            />
          </Group>

          <Text size="xs" c="dimmed">
            İstediğin kalemin yanına miktarı yaz. Boş bırakılan kalemler talebe
            girmez.
          </Text>

          <ScrollArea.Autosize
            mah={mobile ? "calc(100dvh - 360px)" : "55vh"}
            offsetScrollbars
          >
            <Stack gap={0}>
              {groups.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="lg">
                  {search ? "Aramaya uyan kalem yok" : "Henüz ikmal kalemi yok"}
                </Text>
              ) : (
                groups.map((group) => (
                  <div key={group.key}>
                    <Group justify="space-between" mt="md" mb={4}>
                      <Text fw={700} size="sm" c="chocolate.7">
                        {group.name}
                      </Text>
                      {filledIn(group) ? (
                        <Badge size="sm" variant="light" color="chocolate">
                          {filledIn(group)} kalem
                        </Badge>
                      ) : null}
                    </Group>
                    <Divider mb={2} />
                    {group.items.map((item) => (
                      <ItemRow
                        key={item.id}
                        item={item}
                        value={qty[item.id]}
                        onChange={change}
                      />
                    ))}
                  </div>
                ))
              )}
            </Stack>
          </ScrollArea.Autosize>

          <Textarea
            label="Not"
            placeholder="Teslim tarihi, öncelik…"
            autosize
            minRows={1}
            value={note}
            onChange={(event) => setNote(event.currentTarget.value)}
          />

          <FormModalFooter>
            <Text size="sm" c="dimmed" mr="auto">
              {lines.length
                ? `${lines.length} kalem seçildi`
                : "Henüz miktar girilmedi"}
            </Text>
            <Button variant="default" onClick={onClose}>
              Vazgeç
            </Button>
            <Button
              type="submit"
              loading={sending}
              disabled={!branchId || !lines.length}
              leftSection={<IconCheck size={16} />}
            >
              Gönder
            </Button>
          </FormModalFooter>
        </Stack>
      </form>
    </Modal>
  );
}
